import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Checkout() {
  const { cart, refresh, getGuestItems } = useCart();
  const { user } = useAuth();
  const { currency, symbol, unitPrice, unitMrp, isIndia } = useCurrency();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "", notes: "" });
  const [paymentMethod, setPaymentMethod] = useState(isIndia ? "bank_transfer" : "paypal");
  const [bankDetails, setBankDetails] = useState(null);
  const [paypalCfg, setPayPalCfg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState(null);   // internal id awaiting payment
  const [placed, setPlaced] = useState(null);                 // final success state
  const [agreed, setAgreed] = useState(false);

  // Load bank details + PayPal config once
  useEffect(() => {
    api.get("/bank-details").then(({ data }) => setBankDetails(data)).catch(() => {});
    api.get("/payments/paypal/config").then(({ data }) => setPayPalCfg(data)).catch(() => {});
  }, []);

  // Payment method should follow the SELECTED CURRENCY, not just the visitor's
  // geolocation. INR → Bank Transfer / UPI (QR); USD → PayPal. (User can now
  // switch currency freely regardless of geo — payment method must follow suit.)
  useEffect(() => {
    if (currency === "INR") {
      if (paymentMethod !== "bank_transfer") setPaymentMethod("bank_transfer");
    } else {
      // USD / EUR / GBP → PayPal
      if (paymentMethod !== "paypal") setPaymentMethod("paypal");
    }
  }, [currency]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const sym = symbol();

  const total = useMemo(() => {
    if (!cart.items || cart.items.length === 0) return 0;
    return Math.round(cart.items.reduce((s, it) => s + unitPrice(it.book) * it.quantity, 0) * 100) / 100;
  }, [cart.items, unitPrice]);

  const mrpTotal = useMemo(() => {
    if (!cart.items) return 0;
    return Math.round(cart.items.reduce((s, it) => {
      const m = unitMrp(it.book);
      return s + (m > 0 ? m : unitPrice(it.book)) * it.quantity;
    }, 0) * 100) / 100;
  }, [cart.items, unitMrp, unitPrice]);

  const savings = Math.max(0, mrpTotal - total);
  const itemCount = cart.items ? cart.items.reduce((s, it) => s + it.quantity, 0) : 0;

  const placeOrder = async () => {
    if (!agreed) {
      toast.error("Please accept the Return Policy to continue.");
      return null;
    }
    setSubmitting(true);
    try {
      let data;
      if (user) {
        const payload = { ...form, policy_accepted: true, payment_method: paymentMethod, currency };
        ({ data } = await api.post("/orders/checkout", payload));
      } else {
        if (!form.email) { toast.error("Please enter your email"); return null; }
        const items = getGuestItems();
        if (!items.length) { toast.error("Cart is empty"); return null; }
        const payload = { ...form, policy_accepted: true, payment_method: paymentMethod, currency, items };
        ({ data } = await api.post("/orders/guest-checkout", payload));
      }
      return data;
    } catch (err) {
      toast.error(apiError(err));
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  // Bank transfer flow (India only): place order and show success
  const submitBankTransfer = async (e) => {
    e.preventDefault();
    const data = await placeOrder();
    if (!data) return;
    setPlaced({ ...data, payment_method: "bank_transfer", currency });
    await refresh();
  };

  // PayPal flow: 1) form-submit places order & sets placedOrderId, 2) button captures
  const submitPayPalStart = async (e) => {
    e.preventDefault();
    const data = await placeOrder();
    if (!data) return;
    setPlacedOrderId(data);
  };

  if (placed) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="order-success">
        <CheckCircle2 className="w-16 h-16 mx-auto text-[#16a34a] mb-4" />
        <h1 className="font-display text-3xl md:text-4xl font-black mb-2">Order Placed!</h1>
        <div className="text-[#52525b] mb-4">Your order number is</div>
        <div className="font-display text-3xl font-black text-[#ea580c] tracking-widest" data-testid="order-number">{placed.order_number}</div>
        <div className="text-[#52525b] mt-4">Total: {symbol(placed.currency)}{placed.total} · Payment via {placed.payment_method === "paypal" ? "PayPal" : "Bank Transfer"}</div>
        {placed.payment_method === "bank_transfer" && (
          <div className="mt-4 text-xs text-[#7c2d12] max-w-md mx-auto">
            📌 Please complete the bank/UPI transfer and share the receipt on WhatsApp +91 99532 72630. Your order will ship once payment is verified.
          </div>
        )}
        <div className="mt-8 flex gap-3 justify-center">
          <button onClick={() => navigate("/orders")} className="bg-[#09090b] text-white font-bold px-6 py-3" data-testid="view-orders-btn">View Orders</button>
          <button onClick={() => navigate("/books")} className="border-2 border-[#09090b] font-bold px-6 py-3" data-testid="continue-shopping-btn">Continue Shopping</button>
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return <div className="max-w-2xl mx-auto p-10 text-center">Your cart is empty. <button onClick={() => navigate("/books")} className="text-[#ea580c] font-bold">Browse books →</button></div>;
  }

  const showBankOption = currency === "INR"; // Bank Transfer / UPI (QR) for INR only
  const showPayPalOption = currency !== "INR"; // PayPal for USD / EUR / GBP

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="checkout-page">
      <h1 className="font-display text-3xl md:text-5xl font-black border-b-2 border-[#09090b] pb-4 mb-6">Checkout</h1>
      <form onSubmit={paymentMethod === "paypal" ? submitPayPalStart : submitBankTransfer} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-4">
          <div className="border border-border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Shipping Details</div>
              {!user && (
                <div className="text-[10px] text-[#52525b]">
                  Have an account? <Link to="/login" className="text-[#ea580c] font-bold underline">Login</Link>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full Name" value={form.name} onChange={(v) => set("name", v)} required tid="ship-name" />
              <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} required tid="ship-phone" />
            </div>
            <Field label="Email (order updates)" value={form.email} onChange={(v) => set("email", v)} required={!user} tid="ship-email" type="email" placeholder={user ? "leave blank to use your account email" : "your@email.com"} />
            <Field label="Address" value={form.address} onChange={(v) => set("address", v)} required tid="ship-address" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="City" value={form.city} onChange={(v) => set("city", v)} required tid="ship-city" />
              <Field label="State / Region" value={form.state} onChange={(v) => set("state", v)} required tid="ship-state" />
              <Field label={isIndia ? "Pincode" : "Postal / ZIP"} value={form.pincode} onChange={(v) => set("pincode", v)} required tid="ship-pincode" />
            </div>
            <Field label="Order Notes (optional)" value={form.notes} onChange={(v) => set("notes", v)} tid="ship-notes" />
          </div>

          <div className="border border-border p-6" data-testid="payment-methods-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Payment Method · {currency}</div>
              <div className="text-[10px] text-[#71717a]">{isIndia ? "India" : "International"} checkout</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {showBankOption && (
                <label className={`border-2 p-4 flex items-start gap-3 cursor-pointer transition-colors ${paymentMethod === "bank_transfer" ? "border-[#ea580c] bg-[#fff7ed]" : "border-[#e4e4e7] hover:border-[#09090b]"}`} data-testid="pay-bank-transfer">
                  <input type="radio" checked={paymentMethod === "bank_transfer"} onChange={() => setPaymentMethod("bank_transfer")} className="mt-1 accent-[#ea580c]" />
                  <div>
                    <div className="font-bold">Bank Transfer / UPI (QR)</div>
                    <div className="text-xs text-[#52525b] mt-0.5">Scan the Indian Overseas Bank QR below or use NEFT/IMPS/UPI with the account details.</div>
                  </div>
                </label>
              )}
              {showPayPalOption && (
                <label className={`border-2 p-4 flex items-start gap-3 cursor-pointer transition-colors ${paymentMethod === "paypal" ? "border-[#ea580c] bg-[#fff7ed]" : "border-[#e4e4e7] hover:border-[#09090b]"}`} data-testid="pay-paypal">
                  <input type="radio" checked={paymentMethod === "paypal"} onChange={() => setPaymentMethod("paypal")} className="mt-1 accent-[#ea580c]" />
                  <div>
                    <div className="font-bold">PayPal</div>
                    <div className="text-xs text-[#52525b] mt-0.5">Pay securely with your PayPal balance, card, or bank — worldwide.</div>
                  </div>
                </label>
              )}
              {showPayPalOption && (
                <label className={`border-2 p-4 flex items-start gap-3 cursor-pointer transition-colors ${paymentMethod === "bank_transfer" ? "border-[#ea580c] bg-[#fff7ed]" : "border-[#e4e4e7] hover:border-[#09090b]"}`} data-testid="pay-bank-transfer-intl">
                  <input type="radio" checked={paymentMethod === "bank_transfer"} onChange={() => setPaymentMethod("bank_transfer")} className="mt-1 accent-[#ea580c]" />
                  <div>
                    <div className="font-bold">Bank Wire (International)</div>
                    <div className="text-xs text-[#52525b] mt-0.5">SWIFT / wire transfer to our Indian Overseas Bank account. Details shown below.</div>
                  </div>
                </label>
              )}
            </div>

            {/* Bank details + QR panel (shown when Bank Transfer selected) */}
            {paymentMethod === "bank_transfer" && bankDetails && bankDetails.account_number && (
              <div className="mt-4 border-2 border-[#09090b] bg-[#fff7ed] p-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="bank-details-panel">
                <div className="space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ea580c] mb-2">Bank Account</div>
                  <Row k="Bank" v={bankDetails.bank_name} />
                  <Row k="Account Name" v={bankDetails.account_name} />
                  <Row k="A/C No." v={bankDetails.account_number} mono big />
                  <Row k="IFSC" v={bankDetails.ifsc} mono />
                  <Row k="Branch" v={bankDetails.branch} />
                  {bankDetails.swift && <Row k="SWIFT Code" v={bankDetails.swift} mono />}
                  <div className="text-[11px] text-[#7c2d12] mt-3 leading-snug">
                    📌 After payment, WhatsApp the receipt to <a href="https://wa.me/919953272630" className="font-bold underline">+91 99532 72630</a> with your order number.
                  </div>
                </div>
                {isIndia && bankDetails.qr_code_url && (
                  <div className="flex flex-col items-center justify-center border-l md:border-l-2 border-[#ea580c]/30 md:pl-6">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ea580c] mb-2">Scan QR to Pay</div>
                    <img
                      src={bankDetails.qr_code_url}
                      alt="Indian Overseas Bank UPI QR — GAINA THE PRELUDE"
                      className="w-52 h-52 object-contain bg-white border border-[#e4e4e7] p-2"
                      data-testid="bank-qr-image"
                    />
                    <div className="text-[10px] text-[#52525b] mt-2 font-bold">GAINA THE PRELUDE · Indian Overseas Bank</div>
                  </div>
                )}
              </div>
            )}

            {/* PayPal SDK / buttons (shown when PayPal selected + placedOrderId set) */}
            {paymentMethod === "paypal" && (
              <div className="mt-4 border-2 border-[#003087] bg-[#f5f7fa] p-4" data-testid="paypal-panel">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#003087] mb-2">Pay with PayPal · {currency}</div>
                {!placedOrderId && (
                  <div className="text-xs text-[#52525b]">
                    Fill your shipping details above and click <b>Continue to PayPal</b>. Your order will be reserved and the PayPal button will appear here to finalise payment.
                  </div>
                )}
                {placedOrderId && paypalCfg?.enabled && (
                  <PayPalScriptProvider
                    options={{
                      clientId: paypalCfg.client_id,
                      currency: currency,
                      intent: "capture",
                    }}
                  >
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                      createOrder={async () => {
                        try {
                          const { data } = await api.post("/payments/paypal/create-order", { order_id: placedOrderId.id, payment_token: placedOrderId.payment_token });
                          return data.paypal_order_id;
                        } catch (e) {
                          toast.error(apiError(e));
                          throw e;
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          const { data: cap } = await api.post("/payments/paypal/capture", {
                            order_id: placedOrderId.id,
                            paypal_order_id: data.orderID,
                            payment_token: placedOrderId.payment_token,
                          });
                          if (cap.ok) {
                            setPlaced({ ...placedOrderId, payment_method: "paypal", currency });
                            await refresh();
                          }
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}
                      onError={(err) => {
                        toast.error("PayPal error: " + (err?.message || "Please try again"));
                      }}
                      onCancel={() => toast.warning("PayPal payment cancelled — your order is reserved. You can retry.")}
                    />
                  </PayPalScriptProvider>
                )}
                {placedOrderId && !paypalCfg?.enabled && (
                  <div className="text-xs text-red-700">PayPal is not configured. Please contact support.</div>
                )}
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4 border-2 border-[#09090b] p-6 h-fit sticky top-32">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3">Your Order · {currency}</div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {cart.items.map((it) => {
              const p = unitPrice(it.book);
              const m = unitMrp(it.book);
              const lineP = p * it.quantity;
              const lineM = m > 0 ? m * it.quantity : 0;
              const disc = lineM > lineP && lineM > 0 ? Math.round(((lineM - lineP) / lineM) * 100) : 0;
              return (
                <div key={it.book.id} className="text-sm">
                  <div className="flex justify-between gap-2">
                    <div className="truncate"><span className="font-semibold">{it.quantity}×</span> {it.book.title}</div>
                    <div className="shrink-0 font-semibold">{sym}{lineP.toFixed(2)}</div>
                  </div>
                  {lineM > lineP && (
                    <div className="flex justify-end items-baseline gap-2 text-[10px]">
                      <span className="line-through text-[#a1a1aa]">{sym}{lineM.toFixed(2)}</span>
                      {disc > 0 && <span className="text-[#16a34a] font-bold">{disc}% off</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Pricing Details breakdown */}
          <div className="mt-4 pt-3 border-t border-border space-y-1.5 text-sm" data-testid="checkout-breakdown">
            <div className="flex justify-between">
              <span className="text-[#52525b]">Price ({itemCount} {itemCount === 1 ? "Book" : "Books"})</span>
              <span className="font-semibold" data-testid="checkout-mrp-total">{sym}{mrpTotal.toFixed(2)}</span>
            </div>
            {savings > 0 && (
              <div className="flex justify-between">
                <span className="text-[#52525b]">Discount</span>
                <span className="font-semibold text-[#16a34a]" data-testid="checkout-discount">– {sym}{savings.toFixed(2)}</span>
              </div>
            )}
            {!isIndia ? (() => {
              const shippingCost = total < 150 ? 10 : 0;
              const grand = total + shippingCost;
              return (
                <>
                  <div className="flex justify-between">
                    <span className="text-[#52525b]">Shipping</span>
                    <span className="font-semibold">{shippingCost === 0 ? <span className="text-[#16a34a]">FREE</span> : `${sym}${shippingCost.toFixed(2)}`}</span>
                  </div>
                  <div className="pt-2 border-t border-border flex items-baseline justify-between">
                    <div className="font-display font-black">Amount Payable</div>
                    <div className="font-display font-black text-2xl" data-testid="checkout-total">{sym}{grand.toFixed(2)}</div>
                  </div>
                  {total < 150 && (
                    <div className="text-[10px] text-[#7c2d12] bg-[#fff7ed] border border-[#ea580c] p-2 leading-snug mt-2" data-testid="checkout-free-shipping-hint">
                      Add <b>{sym}{(150 - total).toFixed(2)}</b> more for FREE shipping (at $150+).
                    </div>
                  )}
                </>
              );
            })() : (
              <div className="pt-2 border-t border-border flex items-baseline justify-between">
                <div className="font-display font-black">Amount Payable</div>
                <div className="font-display font-black text-2xl" data-testid="checkout-total">{sym}{Math.round(total)}</div>
              </div>
            )}
            {savings > 0 && (
              <div className="bg-[#dcfce7] border border-[#16a34a] p-2.5 text-center text-[#166534] text-sm font-bold mt-2" data-testid="checkout-total-savings">
                Total saving on this order is {sym}{isIndia ? Math.round(savings) : savings.toFixed(2)}
              </div>
            )}
          </div>

          {/* Customs duties notice — visible for any USD/EUR/GBP checkout
              (previously gated on isIndia which hid it when INR was chosen
              by a non-India user; user asked for the notice back). */}
          {(currency !== "INR" || !isIndia) && (
            <div className="mt-4 border-2 border-[#facc15] bg-[#fefce8] p-3" data-testid="customs-notice">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#a16207] mb-1">⚠ Customs Notice</div>
              <div className="text-[11px] text-[#713f12] leading-relaxed">
                International orders may be subject to <b>customs duties and taxes</b>, which must be paid by the <b>buyer upon delivery</b>. These charges are not included in the price shown above.
              </div>
            </div>
          )}

          <label
            className={`mt-5 flex gap-3 p-3 border-2 cursor-pointer transition-colors ${agreed ? "border-[#ea580c] bg-[#fff7ed]" : "border-[#e4e4e7] hover:border-[#09090b]"}`}
            data-testid="return-policy-agree-wrap"
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#ea580c] shrink-0"
              data-testid="return-policy-agree-checkbox"
            />
            <span className="text-xs text-[#3f3f46] leading-relaxed">
              I have read and agree to the{" "}
              <Link to="/return-policy" target="_blank" rel="noreferrer" className="font-bold text-[#ea580c] underline underline-offset-2 hover:text-[#c2410c]" data-testid="return-policy-link-checkout">
                Return &amp; Non-Refundable Policy
              </Link>
              . Damage must be reported right after delivery; refunds are not applicable for change of mind.
            </span>
          </label>

          {!placedOrderId && (
            <button
              type="submit"
              disabled={submitting || !agreed}
              className="mt-5 w-full bg-[#ea580c] hover:bg-[#c2410c] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3"
              data-testid="place-order-btn"
            >
              {submitting ? "Placing…" : (paymentMethod === "paypal" ? "Continue to PayPal" : "Place Order")}
            </button>
          )}
          {placedOrderId && paymentMethod === "paypal" && (
            <div className="mt-5 text-xs bg-[#fff7ed] border border-[#ea580c] p-3 text-[#7c2d12]">
              Order <b>{placedOrderId.order_number}</b> reserved. Complete payment using the PayPal button above.
            </div>
          )}
        </aside>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, tid, type = "text", placeholder }) {
  return (
    <label className="block">
      <div className="text-xs font-bold tracking-wider uppercase text-[#52525b] mb-1.5">{label}{required && " *"}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={tid}
        type={type}
        placeholder={placeholder}
        className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2.5 outline-none text-sm"
      />
    </label>
  );
}

function Row({ k, v, mono, big }) {
  return (
    <div className="text-sm flex flex-wrap gap-x-2">
      <span className="font-bold text-[#52525b]">{k}:</span>
      <span className={`${mono ? "font-mono" : ""} ${big ? "font-bold text-lg" : ""}`}>{v}</span>
    </div>
  );
}
